import {
	Component,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
	ChangeDetectionStrategy,
} from '@angular/core';
import {
	CellClickedEvent,
	ColDef,
	GridApi,
	GridReadyEvent,
	SideBarDef,
} from 'ag-grid-community';
import { CurrencyPipe, Location, NgIf } from '@angular/common';
import { UserService } from '@lmc-app/core/services/user.service';
import { LoanAccountService } from '@lmc-app/core/services/loan-account.service';
import { Router } from '@angular/router';
import { User } from '@lmc-app/shared/interface/user.interface';
import { Subject, Subscription, takeUntil } from 'rxjs';

import 'ag-grid-enterprise';
import {
	MatFormField,
	MatLabel,
	MatInput,
	MatSuffix,
} from '@angular/material/input';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton, MatButton } from '@angular/material/button';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
	selector: 'app-my-funded-deals-dashboard',
	templateUrl: './my-funded-deals-dashboard.component.html',
	styleUrls: ['./my-funded-deals-dashboard.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	encapsulation: ViewEncapsulation.None,
	imports: [
		MatFormField,
		MatLabel,
		MatInput,
		ReactiveFormsModule,
		FormsModule,
		NgIf,
		MatIcon,
		MatSuffix,
		MatIconButton,
		MatButton,
		AgGridAngular,
	],
})
export class MyFundedDealsDashboardComponent implements OnInit, OnDestroy {
	private parentSub = new Subscription();
	private readonly _onDestroy = new Subject<void>();

	public readonly headerStyle: any = {
		display: 'flex',
		'justify-content': 'space-between',
	};

	public columnDefs: ColDef[] = [
		{
			field: 'loan_number',
			headerName: 'Loan Number',
			cellRenderer: (params) => {
				return `<a class="noline">${params.data.loan_number}</a>`;
			},
			width: 150,
		},
		{
			field: 'name',
			headerName: 'Client Name',
			cellRenderer: (params) => {
				return `<a class="noline">${params.data.name}</a>`;
			},
			width: 300,
		},
		{
			field: 'days_past_due',
			headerName: 'Days Past Due',
			width: 150,
		},
		{
			field: 'collection_agency',
			headerName: 'Agency',
			width: 500,
		},
		{
			field: 'creditor',
			headerName: 'Creditor',
			width: 350,
		},
		{
			field: 'lpp_included',
			headerName: 'LPP',
			width: 150,
		},
		{
			field: 'original_note_amount',
			headerName: 'Loan Amount',
			width: 150,
			valueGetter: (params) => {
				return this.currencyFormatter.transform(
					params.data['original_note_amount'],
					'CAD',
					'symbol-narrow'
				);
			},
		},
	];

	private roleID: number = null;
	private currentUser!: User;

	protected rowData: { status: string }[] = [];

	protected sideBar: SideBarDef | string | string[] | boolean | null;

	public constructor(
		private _location: Location,
		private readonly userService: UserService,
		private readonly loanAccountService: LoanAccountService,
		private router: Router,
		private readonly currencyFormatter: CurrencyPipe
	) {}

	public ngOnInit(): void {
		this.userService.user$
			.pipe(takeUntil(this._onDestroy))
			.subscribe((user: User) => {
				this.roleID = user?.roleID;
				this.currentUser = user;
			});
		this.getMyFundedDeals();
	}

	public ngOnDestroy(): void {
		this.parentSub.unsubscribe();
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	protected backClicked() {
		this._location.back();
	}

	private getMyFundedDeals() {
		this.parentSub.add(
			this.loanAccountService
				.getFundedDeals(this.currentUser.employeeID)
				.subscribe((res: any) => {
					this.rowData = res;
				})
		);
	}

	private gridApi: GridApi<any>;

	protected filterValue: string = '';
	protected tooltipShowDelay = 0;
	protected tooltipHideDelay = 2000;

	protected overlayLoadingTemplate =
		'<span class="ag-overlay-loading-center">Loading Data...</span>';
	protected overlayNoRowsTemplate =
		'<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;">This is a custom \'no rows\' overlay</span>';

	protected defaultColDef: ColDef = {
		sortable: true,
		resizable: true,
		filter: true,
		wrapHeaderText: true,
		autoHeaderHeight: true,
	};

	protected onGridReady(params: GridReadyEvent) {
		this.gridApi = params.api;
		this.gridApi.showLoadingOverlay();
	}

	protected onCellClicked(e: CellClickedEvent) {
		const clickedCol = e.colDef.field;
		const { cifnumber } = e.data;
		switch (clickedCol) {
			case 'loan_number':
			case 'name':
				if (this.roleID === 13) {
					return null;
				} else {
					return this.router.navigate(['/applications/summary', cifnumber]);
				}
			default:
				return null;
		}
	}

	protected onFilterTextBoxChanged() {
		this.gridApi.setQuickFilter(this.filterValue);
	}

	protected onFilterClear() {
		this.filterValue = '';
		this.onFilterTextBoxChanged();
	}

	protected resetFilter() {
		this.gridApi?.setFilterModel(null);
		this.onFilterClear();
	}
}
